import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Grid,
    Chip,
    LinearProgress,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import rankingService from "../../services/rankingService";
import learningService from "../../services/learningService";
import creditService from "../../services/creditService";
import { updateDocument } from "../../firebase/firestore";

export default function UserProfilePage() {
    const { userProfile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [rankData, setRankData] = useState(null);
    const [progresses, setProgresses] = useState([]);
    const [credits, setCredits] = useState(0);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editForm, setEditForm] = useState({
        firstName: "",
        lastName: "",
        phone: ""
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                if (userProfile?.uid) {
                    const rank = await rankingService.getUserRank(userProfile.uid);
                    setRankData(rank);

                    const progressData = await learningService.getStudentProgressSummary(
                        userProfile.uid
                    );
                    setProgresses(progressData);

                    const totalCredits = await creditService.getUserTotalCredits(
                        userProfile.uid
                    );
                    setCredits(totalCredits);
                }

                setEditForm({
                    firstName: userProfile?.firstName || "",
                    lastName: userProfile?.lastName || "",
                    phone: userProfile?.phone || ""
                });
            } catch (error) {
                console.error("Error fetching profile data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userProfile]);

    const handleSaveProfile = async () => {
        try {
            await updateDocument("users", userProfile.uid, {
                firstName: editForm.firstName,
                lastName: editForm.lastName,
                phone: editForm.phone
            });
            setOpenEditDialog(false);
        } catch (error) {
            console.error("Error saving profile:", error);
        }
    };

    const getTotalLevelsCleared = () => {
        return progresses.reduce(
            (sum, p) => sum + Object.values(p.levelStatus || {}).filter((s) => s.cleared).length,
            0
        );
    };

    const getCompletedPathsCount = () => {
        return progresses.filter((p) => p.pathCompleted).length;
    };

    if (loading) {
        return (
            <AdminLayout title="Profile">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Profile">
            <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    {/* Profile Card */}
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent sx={{ textAlign: "center" }}>
                                <Avatar
                                    sx={{
                                        width: 100,
                                        height: 100,
                                        mx: "auto",
                                        mb: 2,
                                        backgroundColor: "primary.main"
                                    }}
                                >
                                    {userProfile?.firstName?.charAt(0) || "U"}
                                </Avatar>

                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {userProfile?.firstName} {userProfile?.lastName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {userProfile?.email}
                                </Typography>

                                <Box sx={{ mt: 2, mb: 2 }}>
                                    <Chip
                                        label={userProfile?.collegeName}
                                        size="small"
                                        color="primary"
                                    />
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Button
                                    variant="contained"
                                    startIcon={<EditIcon />}
                                    onClick={() => setOpenEditDialog(true)}
                                    fullWidth
                                >
                                    Edit Profile
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Stats Cards */}
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={2}>
                            {/* Ranking Card */}
                            <Grid item xs={12} sm={6}>
                                <Card sx={{ backgroundColor: "primary.lighter" }}>
                                    <CardContent>
                                        <Typography variant="caption" color="text.secondary">
                                            YOUR GLOBAL RANK
                                        </Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: "primary.main" }}>
                                            #{rankData?.rank || "-"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Out of {rankData?.totalStudents || 0} students
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Credits Card */}
                            <Grid item xs={12} sm={6}>
                                <Card sx={{ backgroundColor: "success.lighter" }}>
                                    <CardContent>
                                        <Typography variant="caption" color="text.secondary">
                                            TOTAL CREDITS
                                        </Typography>
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: "success.main" }}>
                                            {credits}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Points earned
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Learning Progress */}
                            <Grid item xs={12} sm={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                            Learning Progress
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: "primary.main" }}>
                                            {getTotalLevelsCleared()}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Levels Cleared
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Paths Completed */}
                            <Grid item xs={12} sm={6}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                            Learning Paths
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main" }}>
                                            {getCompletedPathsCount()}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Paths Completed
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Learning Paths Details */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                    Learning Progress Details
                                </Typography>

                                {progresses.length === 0 ? (
                                    <Alert severity="info">No learning paths started yet</Alert>
                                ) : (
                                    <Grid container spacing={2}>
                                        {progresses.map((progress) => {
                                            const clearedCount = Object.values(progress.levelStatus || {}).filter(
                                                (s) => s.cleared
                                            ).length;

                                            return (
                                                <Grid item xs={12} sm={6} md={4} key={progress.id}>
                                                    <Card sx={{ backgroundColor: "info.lighter" }}>
                                                        <CardContent>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                                                Path {progress.pathId}
                                                            </Typography>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={(clearedCount / Object.keys(progress.levelStatus || {}).length) * 100 || 0}
                                                                sx={{ mb: 1 }}
                                                            />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {clearedCount} of {Object.keys(progress.levelStatus || {}).length} levels
                                                            </Typography>
                                                            {progress.pathCompleted && (
                                                                <Chip
                                                                    label="Completed"
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{ mt: 1 }}
                                                                />
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Edit Profile Dialog */}
            <Dialog
                open={openEditDialog}
                onClose={() => setOpenEditDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit Profile</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="First Name"
                            value={editForm.firstName}
                            onChange={(e) =>
                                setEditForm((prev) => ({ ...prev, firstName: e.target.value }))
                            }
                        />

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Last Name"
                            value={editForm.lastName}
                            onChange={(e) =>
                                setEditForm((prev) => ({ ...prev, lastName: e.target.value }))
                            }
                        />

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Phone"
                            value={editForm.phone}
                            onChange={(e) =>
                                setEditForm((prev) => ({ ...prev, phone: e.target.value }))
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveProfile}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminLayout>
    );
}
